import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Link } from "@freightsimple/ui-core/Typography/Link";
import { Logo } from "@freightsimple/ui-core/Display/Logo";
import { PasswordInput } from "@freightsimple/ui-core/Controls/PasswordInput";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";
import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { useDispatch, useSelector } from "react-redux";
import { useState } from "react";
import useSignIn from "@/Shared/Authentication/Hooks/useAuthenticationSignIn";
import { setEmailAddressForReset } from "@/Slices/authenticationSlice";
import type { RootState } from "@/store";

interface AuthenticationSignInProps {
  onCreateAccountInstead: () => void;
  onForgotPassword: () => void;
  redirectAfterSignin: boolean;
  onSuccess: () => void;
}

export function AuthenticationSignIn(props: AuthenticationSignInProps) {
  const { onCreateAccountInstead, onForgotPassword, redirectAfterSignin } =
    props;
  const dispatch = useDispatch();
  const { signIn, email, setEmail, password, setPassword, errorMessage } =
    useSignIn();
  const sessionExpired = useSelector(
    (state: RootState) => state.authentication.sessionExpired === true,
  );

  const [loading, setLoading] = useState(false);

  function updateEmail(value: string) {
    setEmail(value);
    dispatch(setEmailAddressForReset(value));
  }

  async function onSignIn() {
    if (loading) {
      return;
    }
    setLoading(true);
    try {
      const success = await signIn(redirectAfterSignin);
      if (success) {
        props.onSuccess();
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <ControlPresentationProvider presentation="underlined">
      {/* Pressing Enter inside any field submits the form; preventDefault stops
          the browser's native GET navigation and drives the same path as the
          button. */}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSignIn();
        }}
        noValidate
      >
        <Stack gap={6}>
          <Stack gap={10}>
            <Logo />
            <Stack gap={1}>
              <Heading level={1} size="4xl" weight="semibold">
                Log into your profile
              </Heading>
              <T size="sm" color="secondary">
                Sign into your FreightSimple profile and simplify your shipping.
              </T>
            </Stack>
          </Stack>

          {sessionExpired && (
            <Banner title="Your session expired">
              Sign in again to pick up where you left off.
            </Banner>
          )}

          <Stack gap={5}>
            <FormItem label="Your Email" fillWidth emphasizeLabel>
              <TextInput
                type="email"
                value={email}
                onChange={(e) => updateEmail(e.target.value)}
                autoComplete="username"
                name="sign_in_email"
                width="full"
                placeholder="you@company.com"
                data-testid="sign-in-email"
              />
            </FormItem>

            <FormItem
              label="Password"
              fillWidth
              emphasizeLabel
              labelAction={
                <Button size="link" onClick={onForgotPassword}>
                  Forgot your password?
                </Button>
              }
            >
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                name="sign_in_password"
                showStrength={false}
                width="full"
                placeholder="Enter your password"
                data-testid="sign-in-password"
              />
            </FormItem>
          </Stack>

          <Stack gap={4}>
            <Button
              primary
              fullWidth
              onClick={onSignIn}
              loading={loading}
              disabled={loading}
              disabledMessage="Signing you in…"
              data-testid="sign-in-button"
            >
              Sign In
            </Button>

            {errorMessage && (
              <T color="red" align="center">
                {errorMessage}
              </T>
            )}

            <Stack align="center">
              <T color="secondary">
                New to FreightSimple?{" "}
                <Link onClick={onCreateAccountInstead}>Create a profile</Link>
              </T>
            </Stack>
          </Stack>
        </Stack>
      </form>
    </ControlPresentationProvider>
  );
}
